<template>
  <section id="mentor-story-section">
    <Card v-if="mentorStoriesSection && mentorStoriesSection.state !== 'hidden'">
      <h2 class="tw-heading-2 tw-mb-4">{{ content.sectionTitle || 'Mentor Story' }}</h2>
      <Card class="tw-bg-primary-50 tw-border-primary-200">
        <div class="tw-flex tw-flex-col tw-items-center tw-text-center tw-space-y-6 tw-py-8 tw-px-6">
          <div class="tw-relative">
            <div class="tw-w-16 tw-h-16 tw-rounded-full tw-bg-gray-300 tw-flex tw-items-center tw-justify-center tw-overflow-hidden">
              <img 
                v-if="mentor.avatarUrl" 
                :src="mentor.avatarUrl" 
                :alt="`${mentor.name}'s avatar`"
                class="tw-w-full tw-h-full tw-object-cover"
                :class="{ 'tw-opacity-50': !isEligible }"
              />
              <div v-else class="tw-w-8 tw-h-8 tw-bg-gray-400 tw-rounded-full" :class="{ 'tw-opacity-50': !isEligible }" />
            </div>
            <div v-if="!isEligible" class="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center">
              <div class="tw-w-8 tw-h-8 tw-rounded-full tw-bg-white tw-shadow-md tw-flex tw-items-center tw-justify-center">
                <LockIcon class="tw-w-4 tw-h-4 tw-text-gray-500" />
              </div>
            </div>
          </div>
          
          <div class="tw-space-y-3">
            <h3 class="tw-text-xl tw-font-semibold tw-text-primary-600">
              {{ content.headline || 'Share your expertise with a mentor story' }}
            </h3>
            <p v-if="isEligible" class="tw-text-base tw-text-primary-600 tw-leading-relaxed">
              {{ content.eligibleDescription || 'Boost your visibility with professional video interview and we\'ll repurpose and promote it.' }}
            </p>
            <p v-else class="tw-text-base tw-text-primary-600 tw-leading-relaxed">
              {{ processIneligibleDescription }}
            </p>
          </div>
          
          <BaseButton
            :color="isEligible ? 'primary' : 'outline-gray'"
            size="large"
            @click="isEligible ? navigateToBooking() : null"
            :disabled="!isEligible"
            class="tw-rounded-full tw-px-8 tw-py-3"
            :class="{ 'tw-cursor-not-allowed': !isEligible }"
          >
            <LockIcon v-if="!isEligible" class="tw-w-4 tw-h-4 tw-mr-2" />
            {{ isEligible ? (content.eligibleButtonText || 'Request your interview') : (content.ineligibleButtonText || 'Unlock with more reviews') }}
          </BaseButton>
        </div>
      </Card>
    </Card>
  </section>
</template>

<script>
import Card from '@/shared/components/Card.vue'
import { computed } from 'vue'
import { router } from '@inertiajs/vue3'
import BaseButton from '@/shared/components/BaseButton.vue'
import LockIcon from '@/assets/images/icons/lock.svg'

export default {
  name: 'MentorStoriesSection',
  components: {
    Card,
    BaseButton,
    LockIcon
  },
  props: {
    mentorStoriesSection: {
      type: Object,
      default: null
    },
    mentor: {
      type: Object,
      required: true
    },
    content: {
      type: Object,
      default: () => ({
        sectionTitle: 'Mentor Story',
        headline: 'Share your expertise with a mentor story',
        eligibleDescription: 'Boost your visibility with professional video interview and we\'ll repurpose and promote it.',
        ineligibleDescription: 'You need {reviewsNeeded} more review{reviewsPlural} to unlock this feature.',
        eligibleButtonText: 'Request your interview',
        ineligibleButtonText: 'Unlock with more reviews'
      })
    }
  },
  setup(props) {
    const reviewsNeeded = computed(() => {
      if (!props.mentorStoriesSection) return 0
      return Math.max(0, 3 - props.mentorStoriesSection.reviewCount)
    })

    const isEligible = computed(() => {
      return reviewsNeeded.value === 0
    })

    const processIneligibleDescription = computed(() => {
      if (!props.content.ineligibleDescription) {
        return `You need ${reviewsNeeded.value} more review${reviewsNeeded.value !== 1 ? 's' : ''} to unlock this feature.`
      }

      return props.content.ineligibleDescription
        .replace('{reviewsNeeded}', reviewsNeeded.value)
        .replace('{reviewsPlural}', reviewsNeeded.value !== 1 ? 's' : '')
    })

    const navigateToBooking = () => {
      router.visit('/mentor-story-booking')
    }

    return {
      reviewsNeeded,
      isEligible,
      processIneligibleDescription,
      navigateToBooking
    }
  }
}
</script> 